// src/hooks/useCurrency.ts
import { useState, useEffect } from 'react';
import { supabase } from '../services/supabase';

export type Currency = 'TJS' | 'RUB' | 'USD' | 'EUR';

const STORAGE_KEY = 'ruyo_exchange_rates';
const CURRENCY_STORAGE_KEY = 'ruyo_currency';
const TTL = 60 * 60 * 1000;

export const CURRENCY_SYMBOLS: Record<Currency, string> = {
    TJS: 'TJS', RUB: '₽', USD: '$', EUR: '€',
};
export const CURRENCY_FLAGS: Record<Currency, string> = {
    TJS: '🇹🇯', RUB: '🇷🇺', USD: '🇺🇸', EUR: '🇪🇺',
};
export const CURRENCY_NAMES: Record<Currency, string> = {
    TJS: 'Сомони', RUB: 'Рубль', USD: 'Доллар', EUR: 'Евро',
};

// ─── Ваши fallback курсы (деление: TJS / rate = валюта) ───────────────────
const FALLBACK_RATES: Record<string, number> = {
    TJS: 1, RUB: 0.115, USD: 10.87, EUR: 11.76,
};

// ─── ГЛОБАЛЬНЫЙ стейт — один экземпляр на всё приложение ─────────────────
let globalRates: Record<string, number> = { ...FALLBACK_RATES };
let globalUpdatedAt: Date | null = new Date();
let globalCurrency: Currency = (() => {
    const saved = localStorage.getItem(CURRENCY_STORAGE_KEY) as Currency;
    return (['TJS', 'RUB', 'USD', 'EUR'] as Currency[]).includes(saved) ? saved : 'TJS';
})();
let ratesLoaded = false;

// Подписчики — уведомляем все компоненты при любом изменении
const subscribers = new Set<() => void>();
const notifyAll = () => subscribers.forEach(fn => fn());

export function useCurrency() {
    const [, forceUpdate] = useState(0);

    // Подписываемся на глобальные изменения (курсы + валюта)
    useEffect(() => {
        const update = () => forceUpdate(n => n + 1);
        subscribers.add(update);
        return () => { subscribers.delete(update); };
    }, []);

    // Загружаем курсы из supabase один раз
    useEffect(() => {
        if (ratesLoaded) return;
        ratesLoaded = true;

        const loadRates = async () => {
            try {
                const cached = localStorage.getItem(STORAGE_KEY);
                if (cached) {
                    const { data, ts } = JSON.parse(cached);
                    if (Date.now() - ts < TTL && data) {
                        globalRates = data;
                        globalUpdatedAt = new Date(ts);
                        notifyAll();
                        return;
                    }
                }
            } catch {}

            try {
                const { data, error } = await supabase
                    .from('exchange_rates')
                    .select('currency, rate_to_tjs, updated_at');

                if (!error && data && data.length > 0) {
                    const newRates: Record<string, number> = { TJS: 1 };
                    let lastUpdated = new Date(0);
                    data.forEach(row => {
                        newRates[row.currency] = Number(row.rate_to_tjs);
                        const d = new Date(row.updated_at);
                        if (d > lastUpdated) lastUpdated = d;
                    });
                    globalRates = newRates;
                    globalUpdatedAt = lastUpdated;
                    localStorage.setItem(STORAGE_KEY, JSON.stringify({
                        data: newRates, ts: Date.now(),
                    }));
                    notifyAll();
                }
            } catch {
                console.warn('[useCurrency] используем fallback курсы');
            }
        };

        loadRates();
    }, []);

    // setCurrency меняет ГЛОБАЛЬНУЮ переменную и уведомляет всех
    const setCurrency = (c: Currency) => {
        globalCurrency = c;
        localStorage.setItem(CURRENCY_STORAGE_KEY, c);
        notifyAll();
    };

    const formatPrice = (priceInTJS: number): string => {
        const rate = globalRates[globalCurrency] ?? 1;
        const converted = priceInTJS / rate;
        switch (globalCurrency) {
            case 'TJS': return Math.round(converted).toLocaleString('ru') + '\u00A0TJS';
            case 'RUB': return '₽\u00A0' + Math.round(converted).toLocaleString('ru');
            case 'USD': return '$\u00A0' + converted.toFixed(2);
            case 'EUR': return '€\u00A0' + converted.toFixed(2);
            default:    return Math.round(converted).toLocaleString('ru') + '\u00A0' + globalCurrency;
        }
    };

    const convert = (priceInTJS: number, toCurrency: Currency = globalCurrency): number => {
        const rate = globalRates[toCurrency] ?? 1;
        return priceInTJS / rate;
    };

    return {
        currency:  globalCurrency,
        setCurrency,
        formatPrice,
        convert,
        rates:     globalRates,
        updatedAt: globalUpdatedAt,
        symbols:   CURRENCY_SYMBOLS,
        flags:     CURRENCY_FLAGS,
        names:     CURRENCY_NAMES,
    };
}